/** @jsx jsx */
import { jsx } from "theme-ui"
import { useMemo, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"

import PropertyTeaser from "../entity/property/propertyTeaser"
import Container from "../../components/container"
import CountyFilter from "./Consumer"
import StatusFilter from "./ConsumerStatus"
import useListingsSearch from "../../hooks/useListingsSearch"

const SearchResults = () => {
  const data = useStaticQuery(graphql`
    {
      counties: allProperty {
        distinct(field: county)
      }
      sanityCounties: allSanityCounty(
        filter: { _id: { regex: "/^((?!drafts).)*$/" } }
      ) {
        nodes {
          countyName
        }
      }
    }
  `)

  const countyOptions = useMemo(() => {
    const set = new Set()
    data.counties.distinct.forEach(c => c && set.add(c))
    data.sanityCounties.nodes.forEach(n => n.countyName && set.add(n.countyName))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [data])

  const [counties, setCounties] = useState([])
  const [statuses, setStatuses] = useState(["for-sale"])

  const params = useMemo(
    () => ({
      county: counties.length ? counties.join(",") : undefined,
      status: statuses.length ? statuses.join(",") : undefined,
      limit: 12,
    }),
    [counties, statuses]
  )

  const { items, isLoading, isLoadingMore, hasMore, loadMore, error, total } =
    useListingsSearch(params)

  return (
    <div className="search-outer">
      <div className="search-outer-inner">
        <div
          id="search-results"
          sx={{
            backgroundColor: "rgba(228, 219, 209, 0.6)",
            padding: "60px 0px 40px 0px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Container>
            <h2
              sx={{
                color: "#635444",
                margin: "0px 0px 20px 0px",
                fontSize: ["2.5rem !important", "3rem !important"],
              }}
            >
              Featured Listings
            </h2>
            <div id="filters">
              <div sx={{ color: "#635444", marginBottom: ["1rem", "10px"] }}>
                Showing {total != null && <span>{total} </span>}Results For:
              </div>
              <div
                sx={{
                  display: "flex",
                  gap: [".5rem", "2rem"],
                  flexDirection: ["column", "row", "row"],
                  color: "#635444",
                  textTransform: "capitalize",
                  maxWidth: ["calc(100% - 20px)", "100%"],
                  "div[class*='multiValue']": {
                    backgroundColor: "transparent",
                    border: "2px solid white",
                    borderRadius: "11px",
                  },
                  "> div": {
                    display: ["block", "flex"],
                    alignItems: "center",
                    marginBottom: ["10px", "0px", "0px"],
                  },
                  ".filter-select": {
                    "> div": {
                      backgroundColor: "#D1C7BC",
                      border: "0px",
                      width: ["100%", "205px"],
                      borderRadius: "20px",
                      overflow: "hidden",
                      color: "#635444 !important",
                      "&:nth-of-type(3)": { marginRight: "0px" },
                      div: {
                        color: "#635444 !important",
                        borderColor: "#635444 !important",
                      },
                      "> div > div": {
                        "div:nth-of-type(2)": {
                          ":hover": {
                            borderTopRightRadius: "8px",
                            borderBottomRightRadius: "8px",
                          },
                        },
                      },
                      "> div:nth-of-type(2)": {
                        backgroundColor: "#635444",
                        width: "40px !important",
                        span: { display: "none" },
                        path: { color: "#635444 !important" },
                        "> div": {
                          height: "100%",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          path: { color: "white !important" },
                        },
                      },
                    },
                  },
                }}
              >
                <div>
                  <div
                    sx={{
                      marginRight: "10px",
                      marginBottom: ["10px", "0px", "0px"],
                    }}
                  >
                    Status:
                  </div>
                  <StatusFilter
                    className="filter-select"
                    value={statuses}
                    onChange={setStatuses}
                  />
                </div>
                <div>
                  <div
                    sx={{
                      marginRight: "1rem",
                      marginBottom: ["10px", "0px", "0px"],
                    }}
                  >
                    Counties:
                  </div>
                  <span>
                    <CountyFilter
                      id="countyselect"
                      className="filter-select"
                      options={countyOptions}
                      value={counties}
                      onChange={setCounties}
                    />
                  </span>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <div className="search-bg" sx={{ paddingTop: 5, paddingBottom: 5 }}>
          <Container noMobilePadding={true}>
            {error && (
              <div sx={{ color: "#95440F", padding: "20px 0", textAlign: "center" }}>
                Couldn't load listings: {error}
              </div>
            )}
            {isLoading && items.length === 0 && (
              <div sx={{ padding: "40px 0", textAlign: "center", color: "#635444" }}>
                Loading listings…
              </div>
            )}
            <ul
              className="ais-InfiniteHits-list"
              sx={{ listStyle: "none", padding: 0, margin: 0 }}
            >
              {items.map(hit => (
                <li
                  sx={{
                    boxSizing: "border-box",
                    zIndex: "1",
                    position: "relative",
                    backgroundColor: "white",
                    marginBottom: "40px",
                  }}
                  key={hit.mls_number || hit.id}
                >
                  <PropertyTeaser
                    property={teaserShape(hit)}
                    className="hit"
                    asModal={true}
                  >
                    {hit.price}
                  </PropertyTeaser>
                </li>
              ))}
            </ul>
            {!isLoading && items.length === 0 && !error && (
              <div sx={{ padding: "40px 0", textAlign: "center", color: "#635444" }}>
                No listings match those filters.
              </div>
            )}
            {hasMore && (
              <button
                sx={{
                  display: "block",
                  backgroundColor: "#95440F",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "1rem",
                  fontWeight: "400",
                  margin: "0 auto 60px",
                  padding: "10px 20px",
                  borderRadius: "18px",
                  "&:hover": { backgroundColor: "#AA693F" },
                  "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                }}
                disabled={isLoadingMore}
                onClick={loadMore}
              >
                {isLoadingMore ? "Loading…" : "Click to load more properties"}
              </button>
            )}
          </Container>
        </div>
      </div>
    </div>
  )
}

// Map an /api/properties row into the shape PropertyTeaser expects
// (it reads address/county/price/acreage/image/slug/displayStatus).
function teaserShape(hit) {
  return {
    ...hit,
    address: hit.address || hit.street_name,
    image: pickImage(hit),
    acreage: hit.lot_size_acres,
    slug: buildSlug(hit),
    displayStatus: hit.status,
  }
}

// MLS detail URLs are /listings/{originating_system_slug}/{mls_number}.
// Sanity-curated rows come through here with just a string slug — those go
// to /listings/{slug}. If a row has no slug (unmapped market), we return
// null so the teaser omits the canonical URL rather than guessing.
function buildSlug(hit) {
  if (hit.source === "sanity") {
    return hit.slug || hit.mls_number || hit.id || null
  }
  if (hit.originating_system_slug && hit.mls_number) {
    return `${hit.originating_system_slug}/${hit.mls_number}`
  }
  return null
}

function pickImage(hit) {
  if (hit.primary_photo_url) return hit.primary_photo_url
  if (Array.isArray(hit.photo_urls) && hit.photo_urls.length) {
    return hit.photo_urls[0]
  }
  if (Array.isArray(hit.photos) && hit.photos.length) {
    const first = hit.photos[0]
    if (typeof first === "string") return first
    if (first && typeof first === "object") {
      return first.url || first.MediaURL || null
    }
  }
  return null
}

export default SearchResults
